import { useMemo } from "react"
import PropTypes from "prop-types"
import { startsWith } from "lodash"
import { MetricsTemplateShape } from "components/custom-metrics-selector/helpers/prop-types"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { computeAttributionMethod } from "helpers/risk"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useImpliedReturns } from "resources/implied-returns"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { FORECAST } from "helpers/shapes/simulator"
import { DEFAULT_HORIZON } from "helpers/forecasts/constants"
import { convertImpliedReturnsIdToUniversalId } from "helpers/securities"
import { useTemplateGatherers } from "../../../dashboard/hooks/use-template-gatherers"
import { useModelId } from "connected-components/dashboard/hooks/use-model-id"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewData } from "connected-components/asset-view/hooks/use-asset-view-data"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"
import { resolveCustomDefaultSort } from "./helpers"

export default function AssetViewCustomComponent(props) {
  const { templateMetadata, customSelectedMetrics, selectedMetrics, instrumentsWithMetadata, modelMetadata, highlightedRowIds, disableActiveTrading, instrumentColumns, contentSets, betaFields, betaInstrument, forecasts, classifications } = props
  const contextData = useAssetViewData()
  const {
    instrument: original,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    earliestDate,
    latestDate,
    performanceStartDate,
    isEndVsStartDateComparison,
    view,
  } = contextData
  const modelId = useModelId()

  const hasImpliedReturns = useMemo(() => {
    return customSelectedMetrics.some((metric) => startsWith(metric.id, "impliedReturns"))
  }, [customSelectedMetrics])

  const [impliedReturns, impliedReturnsStatus] = useImpliedReturns({ modelId, positionSet: original, base: contextBase, from: earliestDate, to: latestDate, hasImpliedReturns })

  const forecastSecurities = useMemo(() => {
    if (impliedReturnsStatus !== "success" || !impliedReturns) {
      return null
    }
    const securities = [...impliedReturns.equities, ...impliedReturns.swaps, ...impliedReturns.indexes]
    return securities.map((security) => ({
      id: convertImpliedReturnsIdToUniversalId({ id: security.id }),
      expectedReturn: { return: security.expectedPercentReturn, horizon: DEFAULT_HORIZON },
    }))
  }, [impliedReturns, impliedReturnsStatus])

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })
  const attributionMethod = computeAttributionMethod(!!contextBase)

  const gatherers = useTemplateGatherers(
    contextData,
    "custom",
    {
      selectedMetrics,
      attributionMethod,
      contentSets,
      betaFields,
      betaInstrument,
      forecasts,
      computedPerformanceStartDate: performanceStartDate,
      forecastSecurities,
      classifications,
    },
  )

  const metrics = useAssetViewDashboardMetrics(
    [{ category: "custom", items: customSelectedMetrics, isEndVsStartDateComparison }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, instrumentsWithMetadata, setTradedPosition, isExpanded, disableActiveTrading, isEndVsStartDateComparison, includeDefaultMetrics: false },
  )

  const filteredMetrics = useMemo(() => {
    return metrics.reduce((acc, metric) => {
      if (metric.dataPath) {
        acc.push(metric)
      }
      return acc
    }, [])
  }, [metrics])

  const defaultSort = useMemo(
    () => resolveCustomDefaultSort(filteredMetrics, instrumentColumns),
    [filteredMetrics, instrumentColumns],
  )

  return <ContributorsDataIntegrator
    instrumentsWithMetadata={instrumentsWithMetadata}
    modelId={modelId}
    mainInstrument={original}
    gatherers={gatherers}
    metrics={filteredMetrics}
    Renderer={ContributorsTable}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      isPositionAttributeGroup: !!groupBy?.isPositionAttribute,
      defaultSort,
      downloadKey: view === "custom" ? "assets.custom" : `assets.custom.${view}`,
      downloadName: `Assets - ${templateMetadata?.name}`,
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
    }}
  />
}

AssetViewCustomComponent.propTypes = {
  modelMetadata: PropTypes.object.isRequired,
  templateMetadata: MetricsTemplateShape,
  customSelectedMetrics: PropTypes.arrayOf(PropTypes.object),
  selectedMetrics: PropTypes.arrayOf(PropTypes.string),
  instrumentsWithMetadata: PropTypes.object.isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  disableActiveTrading: PropTypes.bool.isRequired,
  instrumentColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  classifications: CLASSIFICATIONS_SHAPE.isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  betaFields: PropTypes.arrayOf(PropTypes.string),
  betaInstrument: PropTypes.object,
  forecasts: PropTypes.arrayOf(FORECAST),
}
